import React, { useEffect, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { capture } from 'ducks/tracking/actions'
import { getEditor, COMPOSER_ID, isTextSelected } from 'shared/editor/utils'
import Like from '@groovehq/internal-design-system/lib/assets/icons/facebook-messenger/Like'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Negative from '@groovehq/internal-design-system/lib/assets/icons/Negative'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import {
  button,
  text as textStyle,
} from '@groovehq/internal-design-system/lib/styles/elements'
import EditorButton from 'components/EditorButton'
import SanitizedHTML from 'shared/components/ui/SanitizedHTML'
import { dumbLinkify } from 'util/strings'
import { useAi } from 'ducks/ai/hooks'
import { getRpcMethodFromActionId } from 'ducks/ai/operations'
import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import { buildId } from 'util/globalId'
import { selectIsAddingNote } from 'selectors/page'
import {
  COMPOSER_TYPE,
  TEXT_REFINER_SERVICE,
  SPELLING_ACTION,
  SHORTEN_ACTION,
  LENGTHEN_ACTION,
  FORMAL_ACTION,
  CASUAL_ACTION,
  CLARIFY_ACTION,
} from 'ducks/ai/types'
import { aiWritingTools } from 'components/App/DesktopView/CommunicationForms/Reply/shared/AiButton'
import {
  threeStars as ThreeStars,
  starsCircle as StarsCircle,
} from 'assets/icons/groove/v2'
import colorfulTop from 'assets/colorful-top.png'
import useIsMounted from 'util/hooks/useIsMounted'
import { styles } from './styles'
import Warning from './Warning'

export const AI_COMPOSER_ACTIONS = aiWritingTools.map(tool => tool.id)

export const ALLOWED_AI_ACTIONS = AI_COMPOSER_ACTIONS

const ComposerActionButtons = ({
  handleCopyToComposer,
  handleRemoveAndClose,
  isNote,
  className,
  lastAiType,
  globalTicketId,
  response,
  rating,
}) => {
  const [showThankyou, setShowThankyou] = useState(false)
  const isMounted = useIsMounted()
  const handleOnRating = useCallback(
    buttonRating => {
      setShowThankyou(true)
      capture(`ai ${lastAiType} rated`, {
        ticket_id: globalTicketId,
        rating: buttonRating,
        content: response,
      })
    },
    [lastAiType, response, globalTicketId]
  )

  const handleGoodRating = useCallback(
    () => {
      handleOnRating(1)
    },
    [handleOnRating]
  )

  const handleBadRating = useCallback(
    () => {
      handleOnRating(-1)
    },
    [handleOnRating]
  )

  useEffect(
    () => {
      if (showThankyou) {
        setTimeout(() => {
          if (isMounted()) setShowThankyou(false)
        }, 5000)
      }
    },
    [showThankyou, isMounted]
  )

  return (
    <div className={cn('grui flex items-center', className)}>
      <EditorButton
        type="tertiary"
        size="small"
        css={styles.tertiaryBtn}
        onClick={handleCopyToComposer}
      >
        {`Add to ${isNote ? 'note' : 'reply'}`}
      </EditorButton>
      {
        <button
          css={[button.styles.tertiaryLink, styles.btnLink]}
          className="grui mr-5"
          onClick={handleRemoveAndClose}
        >
          Discard
        </button>
      }
      {!rating &&
        !showThankyou && (
          <div css={styles.aiRatingButtons}>
            <Tooltip title="Good draft" strategy="fixed">
              <Button type="icon" onClick={handleGoodRating} size="small">
                <Like />
              </Button>
            </Tooltip>
            <Tooltip title="Bad draft" strategy="fixed">
              <Button type="icon" onClick={handleBadRating} size="small">
                <Like />
              </Button>
            </Tooltip>
          </div>
        )}
      {showThankyou && (
        <div css={styles.aiRatingDescription} className="grui ml-5">
          Thanks! Your ratings make will make Groove AI smarter.
        </div>
      )}
    </div>
  )
}

const MessageView = ({ className, onLoading }) => {
  const {
    requestAndWait,
    clearState,
    cancelRequest,
    requestState: {
      response,
      loaded,
      error,
      aiType,
      lastAiType,
      loading,
      rating,
    },
  } = useAi(COMPOSER_TYPE)
  const ticketId = useSelector(selectCurrentTicketId)
  const isAddingNote = useSelector(selectIsAddingNote)
  const globalTicketId = buildId('Conversation', ticketId)
  const backgroundImageRef = useRef()
  const containerRef = useRef()
  const previousTicketId = useRef(ticketId)
  const cancelled = useRef(false)

  useEffect(
    () => {
      if (previousTicketId.current !== ticketId) {
        previousTicketId.current = ticketId
        cancelled.current = true
        cancelRequest()
      }
    },
    [ticketId, cancelRequest]
  )

  const handleStopGenerating = useCallback(
    () => {
      capture(`ai ${aiType} canceled`, {
        ticket_id: globalTicketId,
      })
      cancelled.current = true
      cancelRequest()
    },
    [cancelRequest, aiType, globalTicketId]
  )

  const updateEditorFromResponse = useCallback(
    async () => {
      const editor = getEditor(COMPOSER_ID)
      if (cancelled.current || !response || !editor) {
        return
      }
      editor.undoManager.transact(() => {
        if (isTextSelected(editor)) {
          editor.selection.setContent(response)
        } else {
          editor.setContent(response)
        }
      })
      clearState()
    },
    [response, clearState]
  )

  const handleRemoveAndClose = useCallback(
    () => {
      capture(`ai ${lastAiType} discarded`, {
        ticket_id: globalTicketId,
      })
      clearState()
    },
    [clearState, globalTicketId, lastAiType]
  )

  const prepareParamsForComposerType = useCallback(
    id => {
      const editor = getEditor(COMPOSER_ID)
      if (!editor) {
        cancelRequest()
        return false
      }
      const text = isTextSelected(editor)
        ? editor.selection.getContent()
        : editor.getContent()

      switch (id) {
        case SPELLING_ACTION:
          return { text }
        case SHORTEN_ACTION:
          return { length: 'concise', text }
        case LENGTHEN_ACTION:
          return { length: 'verbose', text }
        case FORMAL_ACTION:
          return { tone: 'formal', text }
        case CASUAL_ACTION:
          return { tone: 'casual', text }
        case CLARIFY_ACTION:
          return { text }
        default:
          return {}
      }
    },
    [cancelRequest]
  )

  const doAiRequest = useCallback(
    async () => {
      const params = prepareParamsForComposerType(aiType)
      if (!params) return
      cancelled.current = false
      const method = getRpcMethodFromActionId(aiType)
      capture(`ai ${aiType} request`, {
        ticket_id: globalTicketId,
      })
      try {
        await requestAndWait({
          service: TEXT_REFINER_SERVICE,
          method,
          params,
        })
      } catch (requestError) {
        // The request state renders failures and exposes retry.
      }
    },
    [requestAndWait, aiType, prepareParamsForComposerType, globalTicketId]
  )

  const handleOnTryAgain = useCallback(
    () => {
      doAiRequest()
    },
    [doAiRequest]
  )

  const doAiRequestRef = useRef(doAiRequest)
  useEffect(
    () => {
      doAiRequestRef.current = doAiRequest
    },
    [doAiRequest]
  )

  // doAiRequestRef.current: Prevent doAiRequest changes make the useEffect to run again
  useEffect(
    () => {
      if (aiType && ALLOWED_AI_ACTIONS.includes(aiType)) {
        doAiRequestRef.current()
      }
    },
    [aiType]
  )

  useEffect(
    () => {
      if (loading && onLoading) {
        onLoading()
      }
    },
    [loading, onLoading]
  )

  useEffect(
    () => {
      if (!backgroundImageRef.current && loading) {
        backgroundImageRef.current = colorfulTop
        // Preload the bg
        const img = new Image()
        img.src = colorfulTop
      }
    },
    [loading]
  )

  useEffect(
    () => {
      if (loaded) {
        containerRef.current.scrollIntoView(true)
      }
    },
    [loaded]
  )

  if (!loading && !loaded && !error && !response) return null
  return (
    <div className="grui -mt-19 pt-19" ref={containerRef}>
      <div
        css={[styles.aiContainer, error && styles.error]}
        className={className}
      >
        {loading && (
          <div className="grui flex-center flex-col p-15">
            <StarsCircle width="32" height="32" />
            <div css={styles.loadingCopy}>
              AI Edit in progress<AnimatedEllipsis />
            </div>
            <EditorButton
              type="tertiary"
              size="small"
              onClick={handleStopGenerating}
              css={styles.tertiarySm}
            >
              Cancel
            </EditorButton>
          </div>
        )}
        {error && (
          <div className="grui flex-center flex-col p-10 text-center">
            <Negative width="32" height="32" />
            <div className="grui mt-5 mb-10" css={textStyle.styles.fontMedium}>
              Oh no, something went wrong!
            </div>
            <div className="errorMessage">{error.message}</div>
            <div className="grui mt-10">
              <EditorButton
                type="tertiary"
                size="small"
                onClick={handleOnTryAgain}
                css={styles.tertiaryBtn}
              >
                Try again
              </EditorButton>
              <button
                css={[button.styles.tertiaryLink, styles.btnLink]}
                onClick={handleStopGenerating}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {loaded &&
          response &&
          !cancelled.current && (
            <div>
              <div className="grui flex" css={[styles.header, styles.hasBg]}>
                <ThreeStars />
                <span className="grui ml-5">AI Edit</span>
              </div>
              <div css={styles.body} className="grui flex flex-col pb-6">
                <SanitizedHTML
                  className="body"
                  html={dumbLinkify(response, { showFullUrl: true })}
                />
              </div>
              <div css={styles.footer}>
                <ComposerActionButtons
                  isNote={isAddingNote}
                  handleCopyToComposer={updateEditorFromResponse}
                  handleRemoveAndClose={handleRemoveAndClose}
                  lastAiType={lastAiType}
                  globalTicketId={globalTicketId}
                  response={response}
                  rating={rating}
                />
                <Warning />
              </div>
            </div>
          )}
      </div>
    </div>
  )
}
export default MessageView
